import { useState, useEffect, useMemo } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import {
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  message,
  Tag,
  Tooltip,
  Descriptions,
  Modal,
  Typography,
  Collapse,
  Spin,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useOperatorStore } from '../../store/useOperatorStore';
import {
  DebouncedSelect,
  fetchCustomerList,
} from '../../components/useDebouncedSelectLocation';
import { useGetSapLogs, useGetSapLogDetail } from './Hooks';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_COLORS = {
  SUCCESS: 'green',
  PENDING: 'orange',
  FAILED: 'red',
  SKIPPED: 'default',
};

const APPROVAL_COLORS = {
  PENDING: 'orange',
  APPROVED: 'green',
  SUPERSEDED: 'default',
};

const SapLogsPage = () => {
  const { t } = useTranslation('sap-logs');
  const { operatorId } = useOperatorStore();

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });

  const [filters, setFilters] = useState({
    search: '',
    status: undefined,
    kind: undefined,
    approvalStatus: undefined,
    customerId: undefined,
    dateRange: [dayjs().subtract(30, 'days'), dayjs()],
    billingPeriodRange: null,
  });
  const [searchInput, setSearchInput] = useState('');
  const [filtersApplied, setFiltersApplied] = useState(false);

  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const queryParams = useMemo(() => {
    const params = {
      limit: pagination.limit,
      offset: pagination.page,
    };

    if (operatorId) params.operatorId = operatorId;
    if (filters.status) params.status = filters.status;
    if (filters.kind) params.kind = filters.kind;
    if (filters.approvalStatus) params.approvalStatus = filters.approvalStatus;
    if (filters.customerId) params.customerId = filters.customerId;
    if (filters.search) params.search = filters.search;

    if (filters.dateRange?.[0] && filters.dateRange?.[1]) {
      params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
      params.endDate = filters.dateRange[1].format('YYYY-MM-DD');
    }

    if (filters.billingPeriodRange?.[0] && filters.billingPeriodRange?.[1]) {
      params.periodFrom = filters.billingPeriodRange[0].format('YYYY-MM-DD');
      params.periodTo = filters.billingPeriodRange[1].format('YYYY-MM-DD');
    }

    return params;
  }, [pagination, filters, operatorId]);

  const {
    data: listData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetSapLogs({ params: queryParams });

  const {
    data: detailData,
    isLoading: isDetailLoading,
    error: detailError,
  } = useGetSapLogDetail(selectedId, {
    operatorId,
    enabled: viewModalVisible && !!selectedId,
  });

  const statusOptions = useMemo(
    () => [
      { value: 'SUCCESS', label: t('status_SUCCESS') },
      { value: 'PENDING', label: t('status_PENDING') },
      { value: 'FAILED', label: t('status_FAILED') },
      { value: 'SKIPPED', label: t('status_SKIPPED') },
    ],
    [t]
  );

  const kindOptions = useMemo(
    () => [
      { value: 'OPERATOR_MONTHLY', label: t('kind_OPERATOR_MONTHLY') },
      { value: 'CUSTOMER_PERIOD', label: t('kind_CUSTOMER_PERIOD') },
    ],
    [t]
  );

  const approvalOptions = useMemo(
    () => [
      { value: 'PENDING', label: t('approval_PENDING') },
      { value: 'APPROVED', label: t('approval_APPROVED') },
      { value: 'SUPERSEDED', label: t('approval_SUPERSEDED') },
    ],
    [t]
  );

  const datePresets = useMemo(
    () => [
      { label: t('presets.today'), value: [dayjs(), dayjs()] },
      {
        label: t('presets.yesterday'),
        value: [dayjs().add(-1, 'd'), dayjs().add(-1, 'd')],
      },
      { label: t('presets.last7Days'), value: [dayjs().add(-7, 'd'), dayjs()] },
      {
        label: t('presets.last30Days'),
        value: [dayjs().add(-30, 'd'), dayjs()],
      },
      {
        label: t('presets.thisMonth'),
        value: [dayjs().startOf('month'), dayjs().endOf('month')],
      },
      {
        label: t('presets.lastMonth'),
        value: [
          dayjs().add(-1, 'month').startOf('month'),
          dayjs().add(-1, 'month').endOf('month'),
        ],
      },
    ],
    [t]
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSearchChange = useDebouncedCallback((value) => {
    handleFilterChange('search', value);
  }, 300);

  const handleResetFilters = () => {
    setFilters({
      search: '',
      status: undefined,
      kind: undefined,
      approvalStatus: undefined,
      customerId: undefined,
      dateRange: [dayjs().subtract(30, 'days'), dayjs()],
      billingPeriodRange: null,
    });
    setSearchInput('');
    setFiltersApplied(false);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    const hasExtra =
      !!filters.search ||
      !!filters.status ||
      !!filters.kind ||
      !!filters.approvalStatus ||
      !!filters.customerId ||
      !!filters.billingPeriodRange;
    setFiltersApplied(hasExtra);
  }, [filters]);

  useEffect(() => {
    if (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.status_message ||
        t('fetchError');
      message.error(errorMessage);
    }
  }, [error, t]);

  useEffect(() => {
    if (detailError) {
      const errorMessage =
        detailError?.response?.data?.message ||
        detailError?.response?.data?.status_message ||
        t('fetchError');
      message.error(errorMessage);
    }
  }, [detailError, t]);

  const formatUnixTimestamp = (timestamp) => {
    if (!timestamp) return '—';
    return dayjs.unix(Number(timestamp)).format('YYYY-MM-DD HH:mm:ss');
  };

  const formatBillingPeriod = (from, to, kind) => {
    if (!from || !to) return '—';
    const fromLabel = dayjs.unix(Number(from)).format('YYYY-MM-DD');
    const toLabel = dayjs.unix(Number(to)).format('YYYY-MM-DD');
    if (kind === 'OPERATOR_MONTHLY') {
      return dayjs.unix(Number(from)).format('MMM YYYY');
    }
    return `${fromLabel} – ${toLabel}`;
  };

  const handleViewDetails = (record) => {
    setSelectedId(record._id);
    setViewModalVisible(true);
  };

  const closeModal = () => {
    setViewModalVisible(false);
    setSelectedId(null);
  };

  const renderStatusTag = (status) => {
    if (!status) return '—';
    return (
      <Tag color={STATUS_COLORS[status] || 'default'} variant="outlined">
        {t(`status_${status}`) || status}
      </Tag>
    );
  };

  const renderApprovalCounts = (counts) => {
    if (!counts) return '—';
    const parts = [
      counts.PENDING ? `P:${counts.PENDING}` : null,
      counts.APPROVED ? `A:${counts.APPROVED}` : null,
      counts.SUPERSEDED ? `S:${counts.SUPERSEDED}` : null,
    ].filter(Boolean);
    return parts.length ? parts.join(' ') : '—';
  };

  const columns = [
    {
      title: t('updatedAt'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 170,
      align: 'center',
      render: (v) => (
        <span className="text-sm">{formatUnixTimestamp(v)}</span>
      ),
    },
    {
      title: t('createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      align: 'center',
      render: (v) => (
        <span className="text-sm">{formatUnixTimestamp(v)}</span>
      ),
    },
    {
      title: t('billingPeriod'),
      key: 'billingPeriod',
      width: 160,
      align: 'center',
      render: (_, record) =>
        formatBillingPeriod(record.from, record.to, record.kind),
    },
    {
      title: t('kind'),
      dataIndex: 'kind',
      key: 'kind',
      width: 150,
      align: 'center',
      render: (kind) => t(`kind_${kind}`) || kind || '—',
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      align: 'center',
      render: (status) => renderStatusTag(status),
    },
    {
      title: t('employees'),
      dataIndex: 'employeeCount',
      key: 'employeeCount',
      width: 100,
      align: 'center',
      render: (count) => count ?? 0,
    },
    {
      title: t('approvalCounts'),
      dataIndex: 'approvalCounts',
      key: 'approvalCounts',
      width: 120,
      align: 'center',
      render: (counts) => (
        <span className="text-xs">{renderApprovalCounts(counts)}</span>
      ),
    },
    {
      title: t('httpStatus'),
      dataIndex: 'httpStatus',
      key: 'httpStatus',
      width: 80,
      align: 'center',
      render: (v) => (v == null ? '—' : v),
    },
    {
      title: t('customer'),
      key: 'customer',
      width: 160,
      align: 'center',
      ellipsis: true,
      render: (_, record) => {
        if (record.kind !== 'CUSTOMER_PERIOD') return '—';
        return (
          record.customerName ||
          record.customerEmployeeId ||
          String(record.customerId || '—')
        );
      },
    },
    {
      title: t('errorSkip'),
      key: 'errorSkip',
      width: 200,
      ellipsis: true,
      render: (_, record) => {
        const text = record.error || record.skipReason || '';
        if (!text) return '—';
        return (
          <Tooltip title={text}>
            <span className="text-xs text-red-600">{text}</span>
          </Tooltip>
        );
      },
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 80,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title={t('viewDetails')}>
          <Button
            type="default"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          />
        </Tooltip>
      ),
    },
  ];

  const tableData = listData?.paginatedData || [];
  const totalCount = listData?.totalCount || 0;

  const lineColumns = [
    {
      title: t('employeeId'),
      dataIndex: 'employeeId',
      key: 'employeeId',
      width: 120,
    },
    {
      title: t('customerName'),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 160,
      render: (name, row) => name || row.employeeId || '—',
    },
    {
      title: t('Position'),
      dataIndex: 'position',
      key: 'position',
      width: 140,
      render: (value) => value || '—',
    },
    {
      title: t('factoryNumber'),
      dataIndex: 'factoryNumber',
      key: 'factoryNumber',
      width: 120,
      render: (v) => v || '—',
    },
    {
      title: t('energyKwh'),
      dataIndex: 'energyKwh',
      key: 'energyKwh',
      width: 110,
      align: 'right',
      render: (v) => (Number(v) || 0).toFixed(3),
    },
    {
      title: t('totalAmount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 110,
      align: 'right',
      render: (v) => (Number(v) || 0).toFixed(2),
    },
    {
      title: t('approvalStatus'),
      dataIndex: 'approvalStatus',
      key: 'approvalStatus',
      width: 120,
      render: (status) =>
        status ? (
          <Tag color={APPROVAL_COLORS[status] || 'default'} variant="outlined">
            {t(`approval_${status}`) || status}
          </Tag>
        ) : (
          '—'
        ),
    },
    {
      title: t('approvedAt'),
      dataIndex: 'approvedAt',
      key: 'approvedAt',
      width: 160,
      render: (v) => formatUnixTimestamp(v),
    },
  ];

  const payloadPanels = detailData
    ? [
        {
          key: 'requestXml',
          label: t('requestXml'),
          children: (
            <pre className="bg-gray-50 p-2 rounded text-xs overflow-auto max-h-60 whitespace-pre-wrap">
              {detailData.requestXml || '—'}
            </pre>
          ),
        },
        {
          key: 'soap',
          label: t('soapEnvelopePreview'),
          children: (
            <>
              <Text type="secondary" className="text-xs block mb-2">
                {t('soapPreviewNote')}
              </Text>
              <pre className="bg-gray-50 p-2 rounded text-xs overflow-auto max-h-60 whitespace-pre-wrap">
                {detailData.soapEnvelopePreview || '—'}
              </pre>
            </>
          ),
        },
        {
          key: 'response',
          label: t('responseBody'),
          children: (
            <pre className="bg-gray-50 p-2 rounded text-xs overflow-auto max-h-60 whitespace-pre-wrap">
              {detailData.responseBody || '—'}
            </pre>
          ),
        },
        {
          key: 'tariff',
          label: t('tariffSnapshot'),
          children: (
            <pre className="bg-gray-50 p-2 rounded text-xs overflow-auto max-h-60 whitespace-pre-wrap">
              {detailData.tariffSnapshot
                ? JSON.stringify(detailData.tariffSnapshot, null, 2)
                : '—'}
            </pre>
          ),
        },
      ]
    : [];

  return (
    <div className="space-y-3 h-full">
      <div className="flex items-center gap-2 p-1 w-full overflow-x-auto hide-scrollbar">
        <Input
          placeholder={t('searchPlaceholder')}
          prefix={<SearchOutlined />}
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            handleSearchChange(e.target.value);
          }}
          className="w-full md:!max-w-[220px]"
          allowClear
          onClear={() => {
            setSearchInput('');
            handleFilterChange('search', '');
          }}
        />

        <Select
          placeholder={t('selectStatus')}
          value={filters.status}
          onChange={(value) => handleFilterChange('status', value)}
          allowClear
          className="w-full md:!max-w-[140px]"
          options={statusOptions}
        />

        <Select
          placeholder={t('selectKind')}
          value={filters.kind}
          onChange={(value) => handleFilterChange('kind', value)}
          allowClear
          className="w-full md:!max-w-[160px]"
          options={kindOptions}
        />

        <Select
          placeholder={t('selectApprovalStatus')}
          value={filters.approvalStatus}
          onChange={(value) => handleFilterChange('approvalStatus', value)}
          allowClear
          className="w-full md:!max-w-[150px]"
          options={approvalOptions}
        />

        <DebouncedSelect
          placeholder={t('selectCustomer')}
          value={filters.customerId}
          onChange={(value) => {
            setFilters((prev) => ({
              ...prev,
              customerId: value || undefined,
            }));
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          fetchOptions={(search) =>
            operatorId
              ? fetchCustomerList({ operatorId, search })
              : Promise.resolve([])
          }
          className="w-full md:!max-w-[180px]"
          allowClear
          loadOnOpen
          disabled={!operatorId}
        />

        <RangePicker
          value={filters.dateRange}
          onChange={(value) => handleFilterChange('dateRange', value)}
          format="YYYY-MM-DD"
          className="w-full md:!max-w-[250px]"
          placeholder={[t('createdDateRange'), t('createdDateRange')]}
          presets={datePresets}
        />

        <RangePicker
          value={filters.billingPeriodRange}
          onChange={(value) =>
            handleFilterChange('billingPeriodRange', value)
          }
          format="YYYY-MM-DD"
          className="w-full md:!max-w-[250px]"
          placeholder={[t('billingPeriodRange'), t('billingPeriodRange')]}
          presets={datePresets}
          allowClear
        />

        {filtersApplied && (
          <Button onClick={handleResetFilters}>{t('clear')}</Button>
        )}
        <div>
          <Tooltip title={t('refresh')}>
            <Button
              icon={<ReloadOutlined />}
              loading={isFetching}
              type="default"
              onClick={() => refetch()}
              className="w-full"
            />
          </Tooltip>
        </div>
      </div>

      <div className="w-full">
        <Table
          columns={columns}
          dataSource={tableData}
          rowKey={(record) => record._id}
          loading={isLoading || isFetching}
          pagination={{
            current: pagination.page,
            showSizeChanger: true,
            total: totalCount,
            pageSize: pagination.limit,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, size) => {
              setPagination((prev) => ({ ...prev, page, limit: size }));
            },
            onShowSizeChange: (_current, size) => {
              setPagination((prev) => ({ ...prev, page: 1, limit: size }));
            },
            size: 'default',
            position: ['bottomCenter'],
          }}
          size="middle"
          scroll={{ x: 1700, y: '67vh' }}
          variant="outlined"
          locale={{
            emptyText: t('noSapLogsFound'),
          }}
        />
      </div>

      <Modal
        title={
          <div className="flex items-center gap-2 flex-wrap">
            <EyeOutlined />
            <Title level={4} className="mb-0!">
              {t('sapLogDetails')}
            </Title>
            {detailData && renderStatusTag(detailData.status)}
          </div>
        }
        open={viewModalVisible}
        centered
        onCancel={closeModal}
        width={900}
        footer={[
          <Button key="close" onClick={closeModal}>
            {t('close')}
          </Button>,
        ]}
      >
        {isDetailLoading || !detailData ? (
          <div className="flex justify-center py-10">
            <Spin />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label={t('kind')}>
                {t(`kind_${detailData.kind}`) || detailData.kind || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('status')}>
                {renderStatusTag(detailData.status)}
              </Descriptions.Item>
              <Descriptions.Item label={t('billingPeriod')}>
                {formatBillingPeriod(
                  detailData.from,
                  detailData.to,
                  detailData.kind
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('requestUrl')}>
                {detailData.requestUrl || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('httpStatus')}>
                {detailData.httpStatus == null ? '—' : detailData.httpStatus}
              </Descriptions.Item>
              <Descriptions.Item label={t('employees')}>
                {detailData.employeeCount ?? (detailData.lines?.length || 0)}
              </Descriptions.Item>
              <Descriptions.Item label={t('customer')}>
                {detailData.kind === 'CUSTOMER_PERIOD'
                  ? detailData.customerName ||
                    String(detailData.customerId || '—')
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('error')}>
                {detailData.error || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('skipReason')}>
                {detailData.skipReason || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('createdAt')}>
                {formatUnixTimestamp(detailData.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label={t('updatedAt')}>
                {formatUnixTimestamp(detailData.updated_at)}
              </Descriptions.Item>
              {Array.isArray(detailData.skippedNoEmployeeId) &&
                detailData.skippedNoEmployeeId.length > 0 && (
                  <Descriptions.Item label={t('skippedNoEmployeeId')}>
                    {detailData.skippedNoEmployeeId.length}
                  </Descriptions.Item>
                )}
            </Descriptions>

            <div>
              <Title level={5}>{t('lines')}</Title>
              <Table
                columns={lineColumns}
                dataSource={detailData.lines || []}
                rowKey={(row, index) =>
                  row.uuid || `${row.employeeId}-${index}`
                }
                size="small"
                pagination={{
                  pageSize: 10,
                  hideOnSinglePage: true,
                  position: ['bottomCenter'],
                }}
                scroll={{ x: 900 }}
              />
            </div>

            <Collapse items={payloadPanels} />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SapLogsPage;
